// src/services/azurePendenzenSharesService.js
// Verwaltung externer Read-only-Share-Links für Pendenzen (US-PEND-004, INTRA-Seite).
// Muster: azurePersonsService.js / azureProjektstandService.js (automatische
// Tabellenerstellung, do/while-Paginierung).
//
// SICHERHEITSHINWEIS: Der Link ist UX-Schutz, keine echte Authentifizierung. Die
// Filterung auf `sichtbarExtern` und die Feldreduktion MÜSSEN serverseitig im
// Microservice (US-PEND-006) erfolgen – der SAS-Token-Zugriff kann keine
// zeilenbasierte Sicherheit erzwingen.
import { ensureTableExists } from "./azureTableUtils";

const SHARES_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "pendenzenShares",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Feste Partition, damit der Microservice einen Token ohne Kenntnis des Projekts
// auflösen kann (RowKey = Token).
const SHARE_PARTITION = "share";

// Öffentliche Basis-Adresse des Read-only-Microservice (US-PEND-006). Konfigurierbar
// per Env; Default ist ein Platzhalter, der in US-PEND-006 final gesetzt wird.
const SHARE_BASE_URL = (
  import.meta.env.VITE_PENDENZEN_SHARE_BASE_URL ||
  "https://share.malheur-fortuna.ch/pendenzen"
).replace(/\/+$/, "");

const getBaseUrl = () =>
  `https://${SHARES_CONFIG.storageAccount}.table.core.windows.net/${SHARES_CONFIG.tableName}`;

// Öffentlichen Read-only-Link zu einem Token bauen
export const buildShareUrl = (token) => `${SHARE_BASE_URL}/${token}`;

const mapEntity = (item) => ({
  token: item.RowKey,
  projectUuid: item.ProjectUuid || "",
  createdAt: item.CreatedAt || "",
  createdVon: item.CreatedVon || "",
  expiresAt: item.ExpiresAt || "",
  active: item.Active === true || item.Active === "true",
  url: buildShareUrl(item.RowKey),
});

// Ist ein Share aktuell gültig (aktiv UND nicht abgelaufen)?
export const isShareValid = (share) => {
  if (!share.active) return false;
  if (share.expiresAt) {
    const today = new Date().toISOString().slice(0, 10);
    if (share.expiresAt < today) return false;
  }
  return true;
};

// Alle Share-Links eines Projekts laden (vollständig paginiert)
export const getSharesForProject = async (projectUuid) => {
  if (!projectUuid) return [];
  try {
    await ensureTableExists(SHARES_CONFIG.tableName);

    const filter = `&$filter=PartitionKey eq '${SHARE_PARTITION}' and ProjectUuid eq '${projectUuid}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${getBaseUrl()}()?${SHARES_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .map(mapEntity)
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  } catch (error) {
    console.error("Fehler beim Laden der Share-Links:", error);
    throw new Error("Die Freigabe-Links konnten nicht geladen werden.");
  }
};

// Neuen Share-Link erzeugen (nicht erratbarer GUID als Token)
export const createShare = async (projectUuid, { createdVon = "", expiresAt = "" } = {}) => {
  try {
    await ensureTableExists(SHARES_CONFIG.tableName);

    const token = crypto.randomUUID();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: SHARE_PARTITION,
      RowKey: token,
      ProjectUuid: String(projectUuid || ""),
      CreatedAt: now,
      CreatedVon: String(createdVon || ""),
      ExpiresAt: String(expiresAt || ""),
      Active: true,
    };

    const url = `${getBaseUrl()}?${SHARES_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status}`);
    }

    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Erstellen des Share-Links:", error);
    throw new Error("Der Freigabe-Link konnte nicht erstellt werden.");
  }
};

// Share-Link widerrufen (Active = false) via MERGE
export const revokeShare = async (token) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='${SHARE_PARTITION}',RowKey='${token}')?${SHARES_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        "If-Match": "*",
      },
      body: JSON.stringify({
        PartitionKey: SHARE_PARTITION,
        RowKey: token,
        Active: false,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Widerrufen des Share-Links:", error);
    throw new Error("Der Freigabe-Link konnte nicht widerrufen werden.");
  }
};
